// Shared helpers for anything that renders or compares money from the
// billing API.
//
// See `docs/DUES_FRONTEND_PLAN.md` — this is the first of the foundations
// that document lists. It started early because the violation screens
// needed money formatting when fines moved onto the ledger; the rest of
// it landed with the Dues & Fees epic.
//
// The enum label/colour maps that used to live here are in
// `src/billing/enumLabels.ts`.

import { AgingBuckets, FineReturn } from '../clients/rust-api-client';
import { apiErrorMessage } from './apiErrors';

/**
 * Format a decimal string from the API as US currency.
 *
 * **Money is a string and must never become a number.** Every amount
 * the billing API returns is an exact decimal — `"1250.00"` — and
 * `parseFloat` is the one step that can silently change it. So this
 * groups the integer part with a regex and copies the cents through
 * untouched; nothing here does arithmetic.
 *
 * The sign goes in front of the currency mark (`-$40.00`), which is the
 * form a homeowner or a title company reads without stumbling.
 *
 * `bankHelpers.formatCurrency` takes a `number` and is right for bank
 * balances, which genuinely arrive as floats. It is the wrong tool for
 * a ledger figure.
 */
export const formatMoney = (amount?: string | null): string => {
  if (amount === null || amount === undefined || amount === '') return '-';

  const negative = amount.trim().startsWith('-');
  const digits = amount.trim().replace(/^[-+]/, '');
  const [whole, cents = '00'] = digits.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${negative ? '-' : ''}$${grouped}.${cents.padEnd(2, '0').slice(0, 2)}`;
};

const MONEY_PATTERN = /^[-+]?\d+(\.\d+)?$/;

// `0n` and friends are the natural way to write these, and they do not
// compile: this project targets ES5, where TypeScript rejects a BigInt
// literal outright (TS2737). `BigInt(0)` typechecks and behaves
// identically, so leave the calls alone rather than tidying them.
const ZERO = BigInt(0);
const HUNDRED = BigInt(100);

/**
 * Exact cents for a decimal money string, as a `bigint`.
 *
 * This is the only place the UI is allowed to turn money into a number,
 * and it deliberately produces an integer count of cents rather than a
 * float. Use it wherever the screen genuinely has to add or compare —
 * the aging check below, "total of the selected lines".
 *
 * Throws on anything that is not a decimal number, because a silent `0`
 * would show a total that is wrong rather than a screen that is broken.
 * Empty and null are 0: an absent optional amount is not an error.
 */
export const moneyToCents = (amount?: string | null): bigint => {
  if (amount === null || amount === undefined || amount.trim() === '') {
    return ZERO;
  }
  const value = amount.trim();
  if (!MONEY_PATTERN.test(value)) {
    throw new Error(`Not a decimal money string: ${JSON.stringify(amount)}`);
  }

  const negative = value.startsWith('-');
  const [whole, fraction = ''] = value.replace(/^[-+]/, '').split('.');
  // Two decimal places is the ledger's precision (NUMERIC(12,2)); pad a
  // short fraction, and truncate a long one rather than rounding it,
  // since nothing this side of the API should be inventing a cent.
  const cents =
    BigInt(whole) * HUNDRED + BigInt(fraction.padEnd(2, '0').slice(0, 2));
  return negative ? -cents : cents;
};

/** The inverse of {@link moneyToCents} — a canonical `"1250.00"`. */
export const centsToMoney = (cents: bigint): string => {
  const negative = cents < ZERO;
  const abs = negative ? -cents : cents;
  const whole = abs / HUNDRED;
  const fraction = (abs % HUNDRED).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
};

/** Exact sum of money strings; null, undefined and `''` are skipped. */
export const sumMoney = (
  amounts: ReadonlyArray<string | null | undefined>
): string =>
  centsToMoney(amounts.reduce<bigint>((acc, a) => acc + moneyToCents(a), ZERO));

/** `-1`, `0` or `1`, comparing exactly. */
export const compareMoney = (a?: string | null, b?: string | null): number => {
  const left = moneyToCents(a);
  const right = moneyToCents(b);
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
};

/** True when the amount is below zero — a credit, on this ledger. */
export const isNegativeMoney = (amount?: string | null): boolean =>
  moneyToCents(amount) < ZERO;

/** The amount without its sign, still as a decimal string. */
export const absMoney = (amount?: string | null): string => {
  const cents = moneyToCents(amount);
  return centsToMoney(cents < ZERO ? -cents : cents);
};

/** Whether the aging buckets and the balance agree. */
export type AgingCheck =
  | { status: 'ok' }
  | { status: 'not_applicable' }
  | { status: 'mismatch'; bucketTotal: string; balance: string };

/**
 * The aging buckets sum to the balance, so this is a free client-side
 * sanity check on every screen that shows both.
 *
 * **It does not hold for a credit balance**, and that is not a bug: the
 * buckets age *open charges*, and a home in credit has none, so the
 * total is `0.00` against a negative balance. Such a home returns
 * `not_applicable` rather than a warning nobody can act on. Everything
 * else that disagrees is worth showing quietly — two numbers on one
 * screen that do not reconcile is the thing this exists to catch.
 */
export const agingSumsToBalance = (
  aging?: AgingBuckets | null,
  balance?: string | null
): AgingCheck => {
  if (!aging) return { status: 'not_applicable' };
  if (moneyToCents(balance) < ZERO) return { status: 'not_applicable' };

  const bucketTotal = sumMoney([
    aging.current,
    aging.days_1_30,
    aging.days_31_60,
    aging.days_61_90,
    aging.days_over_90,
  ]);

  if (compareMoney(bucketTotal, balance ?? '0.00') === 0) {
    return { status: 'ok' };
  }
  return { status: 'mismatch', bucketTotal, balance: balance ?? '0.00' };
};

/**
 * A decimal string reduced to one canonical spelling.
 *
 * `"25"`, `"25.0"` and `"25.00"` are the same amount, and a form that
 * treats them as different sends an update for a field nobody touched.
 * Trailing fraction zeros go, a bare point goes, leading integer zeros
 * go (but never the last digit), and an explicit `+` goes. `-0` is zero.
 *
 * Exact, and deliberately not `moneyToCents`: this also has to serve
 * `interest_annual_rate`, which is `NUMERIC(6,4)` — truncating that to
 * cents would call `12.0001%` and `12.00%` the same rate.
 */
export const canonicalDecimal = (value?: string | null): string => {
  if (value === null || value === undefined || value.trim() === '') {
    return '';
  }
  const trimmed = value.trim();
  if (!MONEY_PATTERN.test(trimmed)) return trimmed;

  const negative = trimmed.startsWith('-');
  const [rawWhole, rawFraction = ''] = trimmed.replace(/^[-+]/, '').split('.');
  const whole = rawWhole.replace(/^0+(?=\d)/, '');
  const fraction = rawFraction.replace(/0+$/, '');
  const body = fraction ? `${whole}.${fraction}` : whole;
  return negative && /[1-9]/.test(body) ? `-${body}` : body;
};

/** Whether two decimal strings mean the same number. */
export const sameDecimal = (a?: string | null, b?: string | null): boolean =>
  canonicalDecimal(a) === canonicalDecimal(b);

/**
 * Render a percentage held as a decimal string.
 *
 * `interest_annual_rate` is `"12.0000"` for 12% — a percentage, not a
 * fraction — and `variance_percent` is the same shape. Trailing zeros
 * are trimmed by string surgery rather than by parsing, for the same
 * reason as everything else here.
 */
export const formatPercent = (value?: string | null): string => {
  if (value === null || value === undefined || value === '') return '-';
  const trimmed = value.trim();
  if (!MONEY_PATTERN.test(trimmed)) return trimmed;
  const tidied = trimmed.includes('.')
    ? trimmed.replace(/0+$/, '').replace(/\.$/, '')
    : trimmed;
  return `${tidied}%`;
};

/**
 * Render a unix-seconds timestamp.
 *
 * Billing's `posted_at`, `applied_at`, `updated_at`, `delivered_at` and
 * `date_created` on the HOA are **seconds**. The bank module's
 * `date_created` is a *string*, and `bankHelpers.formatDate` takes one —
 * feeding it a number renders 1970, which is the whole reason these are
 * two separate functions.
 */
export const formatEpochSeconds = (
  seconds?: number | null,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  if (seconds === null || seconds === undefined) return '-';
  const d = new Date(seconds * 1000);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString(undefined, options);
};

/** Just the date part of a unix-seconds timestamp. */
export const formatEpochDate = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined) return '-';
  const d = new Date(seconds * 1000);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

/**
 * Render a `NaiveDate` — `"2026-08-23"` — as a locale date.
 *
 * **Never `new Date("2026-08-23")`.** That parses as UTC midnight, so
 * everywhere west of Greenwich renders the day before. Billing dates
 * are already resolved on the association's own calendar before they
 * leave the backend, so the only correct thing to do here is read the
 * three numbers and show them; there is no timezone left to apply.
 */
export const formatBillingDate = (date?: string | null): string => {
  if (!date) return '-';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date.trim());
  if (!match) return date;
  const [, year, month, day] = match;
  const local = new Date(Number(year), Number(month) - 1, Number(day));
  return isNaN(local.getTime()) ? date : local.toLocaleDateString();
};

/**
 * Debit/credit rendering for a signed ledger amount.
 *
 * **Positive increases what the homeowner owes.** A credit is negative,
 * and callers colour on `isCredit` — mirroring `formatDirectionalAmount`
 * in `bankHelpers`, which does the same job for the bank's `D`/`C`.
 */
export const signedAmountParts = (
  amount?: string | null
): { text: string; isCredit: boolean } => ({
  text: formatMoney(amount),
  isCredit: !!amount && isNegativeMoney(amount),
});

/**
 * Serialise a set of ledger entry types for the ledger's `entry_type`
 * filter, which is **comma-separated** — the backend refuses to read a
 * repeated query parameter, because `serde_urlencoded` keeps only the
 * last one and a filter that silently drops most of itself is worse
 * than no filter. Empty means "no filter", so this returns `undefined`.
 */
export const entryTypeFilter = (
  types?: ReadonlyArray<string> | null
): string | undefined =>
  types && types.length > 0 ? types.join(',') : undefined;

/**
 * Save a PDF the API rendered, using the filename it chose.
 *
 * The three billing documents are rendered server-side so they can be
 * *sent* — a title company needs a file to attach to an email, and
 * `window.print()` is not one. They come back as bytes with a
 * `content-disposition`, so the browser gets the same name the backend
 * picked rather than one invented here.
 *
 * The generated client declares `Accept: application/json` for these,
 * which is wrong but harmless — the server ignores it and the caller
 * passes `responseType: 'blob'`, which is what actually decides how
 * axios reads the body.
 */
export const savePdfResponse = (
  response: { data: unknown; headers?: unknown },
  fallbackName: string
): void => {
  const headers = (response.headers ?? {}) as Record<string, string>;
  const disposition =
    headers['content-disposition'] ?? headers['Content-Disposition'] ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const name = match ? match[1] : fallbackName;

  const blob = response.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * A fresh idempotency key for a money-posting dialog.
 *
 * **Generate one per dialog open, not per submit.** The key lands in
 * `ledger_entries.source_id`, which carries a unique index, so a
 * double-click or a retried request collides and posts once. Minting a
 * new key on each submit would defeat exactly the thing it is for.
 *
 * `crypto.randomUUID` is not present in every environment this runs in
 * (jsdom, older Safari), so there is a fallback — uniqueness within one
 * browser session is all this needs, since the server's index is what
 * actually enforces it.
 */
export const newIdempotencyKey = (): string => {
  const uuid = (globalThis.crypto as { randomUUID?: () => string } | undefined)
    ?.randomUUID;
  if (uuid) return uuid.call(globalThis.crypto);
  return `k-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

/**
 * The message out of a billing API error.
 *
 * Billing handlers answer with a plain-text body — a 409 saying a fine
 * is already settled, a 400 naming the field that will not parse — and
 * those sentences are written to be shown. Since #652 this is
 * `apiErrorMessage` under a name that says billing; it used to fall
 * through to axios's own "Request failed with status code 500".
 */
export const billingErrorMessage = (
  err: unknown,
  fallback = 'Please try again.'
): string => apiErrorMessage(err, fallback);


/**
 * True when a fine still has money owing against it.
 *
 * A fine is an ordinary ledger charge now, so "outstanding" means the
 * charge is neither settled nor withdrawn. Its label and badge colour
 * are `fineStatus` in `src/billing/enumLabels.ts`.
 */
export const fineIsOutstanding = (fine?: FineReturn | null): boolean =>
  !!fine && fine.status !== 'paid' && fine.status !== 'reversed';
